import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Box, 
  SpaceBetween, 
  Button, 
  FormField, 
  Textarea,
  RadioGroup,
  Alert
} from '@cloudscape-design/components';
import { bihGenerateClient } from "../utils/bihClient";

const client = bihGenerateClient();

const FeedbackModal = ({ visible, onDismiss }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [rating, setRating] = useState('3');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
  }, [visible]);

  const handleSubmit = async () => {
    if (!feedbackText.trim()) {
      setError('Please provide feedback text');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const feedbackInput = {
        rating: parseInt(rating),
        comment: feedbackText,
        timestamp: new Date().toISOString()
      };

      // Submit feedback to DynamoDB via AppSync GraphQL API.
      // Backend: bih-feedback-handler Lambda (in bih-feedback CFN stack) writes to
      // BIH-Feedback DDB table and emits the bih.feedback.score EMF metric on every
      // submission. Schema fields match the Feedback type added 2026-05-25.
      const createFeedbackMutation = `
        mutation CreateFeedback($input: CreateFeedbackInput!) {
          createFeedback(input: $input) {
            id
            rating
            comment
            timestamp
            tab
            createdAt
            updatedAt
          }
        }
      `;
      
      const result = await client.graphql({
        query: createFeedbackMutation,
        variables: { input: feedbackInput }
      });
      
      setSuccess(true);
      setFeedbackText('');
      setRating('3');
      
      setTimeout(() => {
        onDismiss();
        setSuccess(false);
      }, 3000); // Auto-dismiss after 3 seconds
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(`Failed to submit feedback: ${err.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Provide Feedback"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit} 
              loading={submitting}
              disabled={submitting || success}
            >
              Submit Feedback
            </Button>
          </SpaceBetween>
        </Box>
      }
      size="medium"
    >
      <SpaceBetween direction="vertical" size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert type="success">
            Thank you for your feedback!
          </Alert>
        )}
        
        <FormField label="How would you rate your experience?">
          <RadioGroup
            value={rating}
            onChange={({ detail }) => setRating(detail.value)}
            items={[
              { value: "1", label: "Very Poor" },
              { value: "2", label: "Poor" },
              { value: "3", label: "Average" },
              { value: "4", label: "Good" },
              { value: "5", label: "Excellent" }
            ]}
          />
        </FormField>
        
        <FormField 
          label="Your feedback" 
          description="Please share your thoughts, suggestions, or report any issues you encountered."
        >
          <Textarea
            value={feedbackText}
            onChange={({ detail }) => setFeedbackText(detail.value)}
            placeholder="Enter your feedback here..."
            rows={5}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default FeedbackModal;
